import { setToken } from 'shared-components/src/utils/auth';
import { getPayAccountUrl } from 'shared-components/src/utils/whitelabel';
import { destinationAfterAuth } from './pinSetup';

export const refresh = async () => 
  // Refresh token is now in HttpOnly cookie, browser sends it automatically
  fetch('/api/v1/cardholder_portal/refresh', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Important: include cookies in request
  })
    .then(res => {
      if (!res.ok) throw new Error('refresh-failed-error');
      return res.json();
    })
    .then(({ exp, token }) => {
      setToken(token, exp);
    })
;

export const login = async (username, password) => fetch('/api/v1/cardholder_portal/login', {
  method: 'POST',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  credentials: 'include', // Important: allow cookies to be set
  body: JSON.stringify({ username, password }),
})
  .then(res => res.json().then(body => {
    if (!res.ok) {
      if (body.error && body.error.code) {
        const error = new Error(body.error.code);
        error.details = body.error.message;
        throw error;
      }
      throw new Error('login-failed-error');
    }
    return body;
  }))
  .then((body) => {
    // Store the token in all cases
    setToken(body.token, body.exp);

    // Clear any stale MFA session data from previous sessions
    sessionStorage.removeItem('mfa_user_sub');
    sessionStorage.removeItem('mfa_setup_data');

    // Handle MFA flow based on login response
    if (body.mfa_required) {
      if (body.mfa_registered === false) {
        // Store MFA setup data for registration
        sessionStorage.setItem('mfa_setup_data', JSON.stringify({
          secret_code: body.secret_code,
          session: body.session,
          user_sub: body.user_sub,
          user_name: body.user_name
        }));
        window.location.pathname = '/mfa-setup';
        return { mfa_required: true, mfa_registered: false };
      }

      if (body.mfa_registered === true) {
        // Store user_sub for MFA verification
        sessionStorage.setItem('mfa_user_sub', body.user_sub);
        window.location.pathname = '/mfa-verify';
        return { mfa_required: true, mfa_registered: true };
      }

      // Default to setup if mfa_registered is not explicitly true
      sessionStorage.setItem('mfa_setup_data', JSON.stringify({
        secret_code: body.secret_code,
        session: body.session,
        user_sub: body.user_sub,
        user_name: body.user_name
      }));
      window.location.pathname = '/mfa-setup';
      return { mfa_required: true, mfa_registered: false };
    }

    // Programs without MFA land here straight after registration, so the
    // registration PIN step (when pending) comes before the dashboard.
    window.location.pathname = destinationAfterAuth();
    return { mfa_required: false };
  });

export const camsLogin = async (cardNumber, cvv, accessCode) => fetch('/api/v1/cardholder_portal/cams_login', {
  method: 'POST',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  credentials: 'include',
  body: JSON.stringify({ card_number: cardNumber, cvv, access_code: accessCode }),
})
  .then(res => res.json().then(body => {
    if (!res.ok) {
      if (body.error && body.error.code) {
        const error = new Error(body.error.code);
        error.details = body.error.message;
        throw error;
      }
      throw new Error('cams-login-failed-error');
    }
    return body;
  }))
  .then((body) => {
    // Check if user needs to register (CAMS migration flow)
    if (body.needs_registration) {
      // Store the token only for users who need to register
      setToken(body.token, body.exp);

      // Clear any stale MFA session data from previous sessions
      sessionStorage.removeItem('mfa_user_sub');
      sessionStorage.removeItem('mfa_setup_data');

      window.location.pathname = '/register';
      return { needs_registration: true };
    }

    // User is already registered - redirect to login page on payaccount.io
    window.location.href = `${getPayAccountUrl()}/login`;
    return { already_registered: true };
  });

export const forgotPassword = async (payload, setEmailSent) => fetch('/api/v1/cardholder_portal/forgot_password', {
  method: 'POST',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify(payload),
}).then(res => res.json().then(body => {
  if (!res.ok) {
    setEmailSent(false);
    throw new Error(body.error.message || 'An unexpected error occurred');
  } else {
    return setEmailSent(true);
  }
}));

export const forgotUsername = async payload => fetch('/api/v1/cardholder_portal/forgot_username', {
  method: 'POST',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify(payload),
}).then(async res => {
  // a non-JSON body must not become the thrown error
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error?.code || 'forgot_username_failed');
  return body.data;
});

export const resetPassword = async payload => fetch('/api/v1/cardholder_portal/reset_password', {
  method: 'POST',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify(payload),
})
  .then(res => res.json())
  .then(body => {
    if (body.error) throw new Error(body.error.message);
    return body;
  });

export const changePassword = async payload =>
  fetch('/api/v1/cardholder_portal/change_password', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  })
    .then(res => res.json())
    .then(body => {
      if (body.error) throw new Error(body.error.message);
      return body;
    });

export const validateToken = async pwt => fetch(`/api/v1/cardholder_portal/reset_password?token=${pwt}`)
  .then(res => res.json())
  .then(body => {
    if (body.error) throw new Error(body.error.message);
    return body;
  });

// MFA Functions
export const getMFAStatus = async () => {
  // Check if we have stored user_sub from login (for verify flow)
  const userSub = sessionStorage.getItem('mfa_user_sub');

  if (userSub) {
    // User is in verify flow - return registered status
    return {
      mfa_registered: true,
      user_sub: userSub
    };
  }

  // Check if we have stored setup data from login (for setup flow)
  const setupData = sessionStorage.getItem('mfa_setup_data');

  if (setupData) {
    try {
      const data = JSON.parse(setupData);
      return {
        mfa_registered: false,
        secret_code: data.secret_code,
        session: data.session,
        user_sub: data.user_sub
      };
    } catch (error) {
      throw new Error('mfa-setup-data-invalid');
    }
  }

  // No MFA data available
  throw new Error('mfa-data-not-available');
};

export const completeMFASetup = async (userSub, session, totpCode) =>
  fetch('/api/v1/cardholder_portal/mfa/setup', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ user_sub: userSub, session, totp_code: totpCode }),
  })
    .then(res => res.json().then(body => {
      if (!res.ok) {
        if (body.error && body.error.code) {
          const error = new Error(body.error.code);
          error.details = body.error.message;
          throw error;
        }
        throw new Error('mfa-setup-failed');
      }
      return body;
    }))
    .then(({ exp, token }) => {
      // Store full access token after successful MFA setup
      setToken(token, exp);
      // Clear MFA session data
      sessionStorage.removeItem('mfa_setup_data');
      sessionStorage.removeItem('mfa_user_sub');
      window.location.pathname = '/';
    });

export const verifyMFA = async (userSub, totpCode) =>
  fetch('/api/v1/cardholder_portal/mfa/verify', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ user_sub: userSub, totp_code: totpCode }),
  })
    .then(res => res.json().then(body => {
      if (!res.ok) {
        if (body.error && body.error.code) {
          const error = new Error(body.error.code);
          error.details = body.error.message;
          throw error;
        }
        throw new Error('mfa-verification-failed');
      }
      return body;
    }))
    .then(({ exp, token }) => {
      // Store full access token after successful MFA verification
      setToken(token, exp);
      // Clear MFA session data
      sessionStorage.removeItem('mfa_setup_data');
      sessionStorage.removeItem('mfa_user_sub');
      window.location.pathname = '/';
    });